import { useEffect, useState } from "react";
import { Box, Button, Flex, Paper, Text, ThemeIcon, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronRight, IconCoins, IconGift } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";

/** Matches primary proceed button (`Proceed with your Credits`). */
const NAVY = "#04305c";

const FIRST_COLLAB_STYLES = {
  card: {
    border: "1px solid #86EFAC",
    backgroundColor: "#F0FDF4",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  },
  iconWrap: { backgroundColor: "#DCFCE7", color: "#15803D" },
  creditBox: {
    border: `1px solid ${NAVY}`,
    backgroundColor: "#F0FDF4",
  },
  proceedBtn: {
    root: {
      backgroundColor: "#86EFAC",
      borderColor: "#86EFAC",
      color: "#0F172A",
      fontWeight: 700,
      "&:hover": { backgroundColor: "#4ADE80", borderColor: "#4ADE80" },
    },
  },
} as const;

const STANDARD_STYLES = {
  card: {
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFFBEB",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  },
  iconWrap: { backgroundColor: "#FEF3C7", color: "#B45309" },
  creditBox: {
    border: `1px solid ${NAVY}`,
    backgroundColor: "#FFFBEB",
  },
  proceedBtn: {
    root: {
      backgroundColor: NAVY,
      borderColor: NAVY,
      color: "#ffffff",
      fontWeight: 600,
      "&:hover": { backgroundColor: "#032847", borderColor: "#032847" },
    },
  },
} as const;

type CollaborationPaymentHeaderCardProps = {
  creditsRequired: number;
  onProceed: () => void;
  loading?: boolean;
  /** Viewer already paid — hide proceed, show waiting copy. */
  userPaid?: boolean;
  /** When omitted, fetched from wallet (engagementFee debits === 0 → first collaboration). */
  isFirstCollaboration?: boolean;
  availableCredits?: number;
};

function CreditSummary({
  availableCredits,
  creditsRequired,
  isFirstCollaboration,
  loading,
  compact = false,
}: {
  availableCredits: number;
  creditsRequired: number;
  isFirstCollaboration: boolean;
  loading?: boolean;
  compact?: boolean;
}) {
  const availableDisplay = loading ? "—" : String(availableCredits);
  const styles = isFirstCollaboration ? FIRST_COLLAB_STYLES : STANDARD_STYLES;
  const labelColor = NAVY;
  const valueColor = NAVY;

  return (
    <Flex
      gap={0}
      align="stretch"
      w={compact ? "100%" : undefined}
      style={{
        ...styles.creditBox,
        borderRadius: 10,
        flexShrink: 0,
        minWidth: compact ? 0 : 320,
        maxWidth: "100%",
      }}
    >
      <Box px={compact ? "md" : "lg"} py={6} style={{ flex: 1, minWidth: compact ? 0 : 148, textAlign: "center" }}>
        <Text
          fz="xs"
          mb={2}
          style={{ whiteSpace: "nowrap", color: labelColor }}
        >
          Available Credits
        </Text>
        <Text fz="lg" fw={700} lh={1.1} style={{ color: valueColor }}>
          {availableDisplay}
        </Text>
      </Box>
      <Box w={1} style={{ backgroundColor: "#E5E7EB", alignSelf: "stretch" }} />
      <Box px={compact ? "md" : "lg"} py={6} style={{ flex: 1, minWidth: compact ? 0 : 148, textAlign: "center" }}>
        <Text
          fz="xs"
          mb={2}
          style={{ whiteSpace: "nowrap", color: labelColor }}
        >
          Credits to be used
        </Text>
        <Text fz="lg" fw={700} lh={1.1} style={{ color: valueColor }}>
          {creditsRequired}
        </Text>
      </Box>
    </Flex>
  );
}

export default function CollaborationPaymentHeaderCard({
  creditsRequired,
  onProceed,
  loading = false,
  userPaid = false,
  isFirstCollaboration: isFirstCollaborationProp,
  availableCredits: availableCreditsProp,
}: CollaborationPaymentHeaderCardProps) {
  const theme = useMantineTheme();
  const isCompact = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );

  const [availableCredits, setAvailableCredits] = useState(availableCreditsProp ?? 0);
  const [isFirstCollaboration, setIsFirstCollaboration] = useState(isFirstCollaborationProp ?? false);
  const [balanceLoading, setBalanceLoading] = useState(
    availableCreditsProp === undefined || isFirstCollaborationProp === undefined,
  );

  useEffect(() => {
    if (availableCreditsProp !== undefined && isFirstCollaborationProp !== undefined) {
      setAvailableCredits(availableCreditsProp);
      setIsFirstCollaboration(isFirstCollaborationProp);
      setBalanceLoading(false);
      return;
    }

    let cancelled = false;
    setBalanceLoading(true);

    trpc.wallet.getBalance
      .query()
      .then((wallet) => {
        if (cancelled) return;
        setAvailableCredits(wallet.available);
        if (isFirstCollaborationProp !== undefined) {
          setIsFirstCollaboration(isFirstCollaborationProp);
        } else {
          setIsFirstCollaboration((wallet.breakdown?.engagementFee ?? 0) === 0);
        }
      })
      .catch(() => {
        if (!cancelled) setAvailableCredits(0);
      })
      .finally(() => {
        if (!cancelled) setBalanceLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [availableCreditsProp, isFirstCollaborationProp]);

  const variant = isFirstCollaboration ? FIRST_COLLAB_STYLES : STANDARD_STYLES;

  const title = userPaid
    ? "Waiting for partner payment"
    : isFirstCollaboration
      ? "Your first collaboration is on us!"
      : "Collaborations use credits";

  const subtitle = userPaid
    ? "You see anonymized partner details until they pay. Chat, documents, and contact info unlock after both parties pay."
    : isFirstCollaboration
      ? "You are viewing full company details, and all workspace features are unlocked."
      : `You are viewing full company details. ${creditsRequired} collaboration credits will be used for this collaboration.`;

  const proceedLabel = isFirstCollaboration ? "Proceed with Free Credits" : "Proceed with Your Credits";

  return (
    <Box mb={isCompact ? "sm" : "md"} style={{ position: "relative" }}>
      <Paper radius="lg" p={isCompact ? "sm" : "md"} style={variant.card}>
        <Flex
          justify="space-between"
          align={isCompact ? "stretch" : { base: "stretch", md: "center" }}
          gap={isCompact ? "sm" : "md"}
          direction={isCompact ? "column" : { base: "column", md: "row" }}
          wrap="nowrap"
        >
          <Flex gap="sm" align="flex-start" miw={0} style={{ flex: 1 }}>
            <ThemeIcon
              size={isCompact ? 32 : 40}
              radius="xl"
              variant="light"
              style={{ flexShrink: 0, ...variant.iconWrap }}
            >
              {isFirstCollaboration && !userPaid ? (
                <IconGift size={isCompact ? 18 : 20} stroke={1.75} />
              ) : (
                <IconCoins size={isCompact ? 18 : 20} stroke={1.75} />
              )}
            </ThemeIcon>
            <Box miw={0}>
              <Text fw={700} fz={isCompact ? "xs" : "sm"} c="#0F172A" lh={1.35}>
                {title}
              </Text>
              <Text fz={isCompact ? "xs" : "sm"} c="#334155" mt={isCompact ? 2 : 4} lh={1.4}>
                {subtitle}
              </Text>
            </Box>
          </Flex>

          {!userPaid ? (
            <Flex
              align="stretch"
              gap={isCompact ? "xs" : "md"}
              direction={isCompact ? "column" : "row"}
              wrap="wrap"
              justify={isCompact ? "stretch" : { base: "stretch", md: "flex-end" }}
              w={isCompact ? "100%" : undefined}
              style={{ flexShrink: 0 }}
            >
              <CreditSummary
                availableCredits={availableCredits}
                creditsRequired={creditsRequired}
                isFirstCollaboration={isFirstCollaboration}
                loading={balanceLoading}
                compact={isCompact}
              />
              <Button
                radius="md"
                size={isCompact ? "sm" : "md"}
                onClick={onProceed}
                loading={loading || balanceLoading}
                styles={variant.proceedBtn}
                rightSection={
                  !isFirstCollaboration ? <IconChevronRight size={16} stroke={2.5} /> : undefined
                }
                w={isCompact ? "100%" : undefined}
                style={{ flexShrink: 0 }}
              >
                {proceedLabel}
              </Button>
            </Flex>
          ) : null}
        </Flex>
      </Paper>
      {isFirstCollaboration && !userPaid ? (
        <Box
          aria-hidden
          style={{
            position: "absolute",
            right: 24,
            top: 8,
            width: 48,
            height: 48,
            pointerEvents: "none",
            opacity: 0.35,
            background:
              "radial-gradient(circle at 30% 40%, #FDE047 2px, transparent 2px), radial-gradient(circle at 70% 60%, #94A3B8 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />
      ) : null}
    </Box>
  );
}
